import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import DepartmentsList from '../components/admin/departments/DepartmentsList';
import DepartmentForm from '../components/admin/departments/DepartmentForm';
import toast from 'react-hot-toast';
import DepartmentOrgChart from '../components/admin/departments/DepartmentOrgChart';
import { TableCellsIcon, ChartBarIcon } from '@heroicons/react/24/outline';

// Define the structure of a Department
export interface Department {
    id: string;
    name: string;
    parentId: string | null;
    // This field will store the full hierarchical path for easy searching.
    // e.g., "UI/UX > Frontend > Development"
    completeDescription: string;
    children?: Department[];
    responsibleId?: string;
    responsibleName?: string;
}

const DepartmentsPage: React.FC = () => {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');

    const fetchDepartments = useCallback(async () => {
        setIsLoading(true);
        try {
            const departmentsSnapshot = await getDocs(collection(db, 'departments'));
            const departmentsList = departmentsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Department));
            setDepartments(departmentsList);

        } catch (err: any) {
            console.error("Error fetching data: ", err);
            setError("Failed to fetch initial data.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const handleOpenForm = (department: Department | null = null) => {
        setEditingDepartment(department);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setEditingDepartment(null);
        setIsFormOpen(false);
    };

    const buildCompleteDescription = (name: string, parent: Department | null): string => {
        if (parent) {
            // Prepend the new department name to the parent's full description
            return `${name} < ${parent.completeDescription}`;
        }
        return name;
    };

    const handleSave = async (departmentData: Omit<Department, 'id' | 'completeDescription' | 'children'>, parent: Department | null) => {
        setIsSubmitting(true);
        const completeDescription = buildCompleteDescription(departmentData.name, parent);
        const dataToSave = {
            name: departmentData.name,
            parentId: departmentData.parentId,
            responsibleId: departmentData.responsibleId || null,
            responsibleName: departmentData.responsibleName || null,
            completeDescription
        };

        try {
            if (editingDepartment) {
                // Update
                const departmentRef = doc(db, 'departments', editingDepartment.id);
                await updateDoc(departmentRef, dataToSave);

                // Recursively update children
                await updateChildrenDescriptions(editingDepartment.id, dataToSave.completeDescription);

                toast.success('Departamento atualizado com sucesso!');
            } else {
                // Create
                await addDoc(collection(db, 'departments'), dataToSave);
                toast.success('Departamento criado com sucesso!');
            }
            await fetchDepartments(); // Refresh list
            handleCloseForm();
        } catch (err: any) {
            console.error("Error saving department: ", err);
            toast.error(`Erro ao salvar departamento: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateChildrenDescriptions = async (parentId: string, parentCompleteDescription: string) => {
        const children = departments.filter(d => d.parentId === parentId);
        for (const child of children) {
            const newCompleteDescription = `${child.name} < ${parentCompleteDescription}`;
            const childRef = doc(db, 'departments', child.id);
            await updateDoc(childRef, { completeDescription: newCompleteDescription });
            // Recursively update this child's children
            await updateChildrenDescriptions(child.id, newCompleteDescription);
        }
    };

    const handleDelete = async (departmentId: string) => {
        // First, check if the department has any children.
        const hasChildren = departments.some(d => d.parentId === departmentId);
        if (hasChildren) {
            toast.error("Não é possível excluir um departamento que possui sub-departamentos.");
            return;
        }

        if (window.confirm("Tem certeza que deseja excluir este departamento?")) {
            try {
                await deleteDoc(doc(db, 'departments', departmentId));
                toast.success("Departamento excluído com sucesso!");
                await fetchDepartments();
            } catch (err: any) {
                console.error("Error deleting department: ", err);
                toast.error(`Erro ao excluir departamento: ${err.message}`);
            }
        }
    };

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-4">
                    <h1 className="text-2xl font-bold">Departamentos</h1>
                    <div className="flex items-center rounded-md bg-gray-200 p-1">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-gray-300'}`}
                            aria-label="Visualização em Lista"
                        >
                            <TableCellsIcon className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => setViewMode('chart')}
                            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${viewMode === 'chart' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-gray-300'}`}
                            aria-label="Visualização em Organograma"
                        >
                            <ChartBarIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <button
                    onClick={() => handleOpenForm()}
                    className="btn-primary"
                >
                    Adicionar Departamento
                </button>
            </div>

            {isFormOpen && (
                <div className="mb-6">
                    <DepartmentForm
                        department={editingDepartment}
                        departments={departments}
                        onSave={handleSave}
                        onCancel={handleCloseForm}
                        isSubmitting={isSubmitting}
                    />
                </div>
            )}

            {isLoading && <p>Carregando departamentos...</p>}
            {error && <p className="text-red-500">{error}</p>}

            {!isLoading && !error && !isFormOpen && (
                viewMode === 'list' ? (
                    <DepartmentsList
                        departments={departments}
                        onEdit={handleOpenForm}
                        onDelete={handleDelete}
                    />
                ) : (
                    <DepartmentOrgChart departments={departments} />
                )
            )}
        </div>
    );
};

export default DepartmentsPage; 